import React, { useEffect, useState, useContext, useMemo } from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box"; // Import Box
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, useTheme } from "@mui/material/styles";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch"; // Add Switch import
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider"; // Import Divider
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import AccountCircle from "@mui/icons-material/AccountCircle";
import ChatIcon from "@mui/icons-material/Chat";
import { Dashboard as DashboardIcon } from "@mui/icons-material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { People as PeopleIcon } from "@mui/icons-material";
import { Person as PersonIcon } from "@mui/icons-material";
import { Today as TodayIcon } from "@mui/icons-material";
import { Insights as InsightsIcon } from "@mui/icons-material";
import PsychologyAltIcon from "@mui/icons-material/PsychologyAlt";
import SelfImprovementIcon from "@mui/icons-material/SelfImprovement";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import { useLocation, useNavigate, Link } from "react-router-dom"; // Import useLocation and Link
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "../../firebase/firebase.js";
import logo from "../../assets/1Coach.png"; // Adjust path if necessary
import { ImageButtonsContext } from "../../context/ImageButtonsContext";
import { TimerContext } from "../../context/TimerContext";
import { PointsVisibilityContext } from "../../context/PointsVisibilityContext"; // Added
import { NodeButtonsContext } from "../../context/NodeButtonsContext";
import { ImageVisibilityContext } from "../../context/ImageVisibilityContext";
import DeleteAccountDialog from "../profile/DeleteAccountDialog";
import { deleteAccount } from "../../services/accountService";
import { ChatVisibilityContext } from "../../context/ChatVisibilityContext";
import { TutorialContext } from "../../tutorial/TutorialContext"; // Import TutorialContext

const Navbar = ({ mode, toggleColorMode }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const { showImageButtons, setShowImageButtons } =
    useContext(ImageButtonsContext);
  const { showTimer, setShowTimer } = useContext(TimerContext);
  const { showPoints, setShowPoints } = useContext(PointsVisibilityContext); // Changed toggleShowPoints to setShowPoints
  const { showNodeButtons, setShowNodeButtons } =
    useContext(NodeButtonsContext);
  const { showImages, setShowImages } = useContext(ImageVisibilityContext);
  const { chatExpanded, setChatExpanded } = useContext(ChatVisibilityContext);
  const { startTutorial, resetTutorialProgress } = useContext(TutorialContext); // Get startTutorial and resetTutorialProgress from context
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation(); // Added useLocation hook
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const isPortrait = useMediaQuery("(orientation: portrait)");

  const accentTone = useMemo(
    () =>
      theme.palette.secondary?.main ??
      theme.palette.primary.light ??
      theme.palette.primary.main,
    [theme]
  );

  const barBackground = useMemo(() => {
    const base =
      theme.palette.mode === "light"
        ? alpha(theme.palette.background.paper, 0.92)
        : alpha(theme.palette.background.default, 0.75);
    const primaryGlow = alpha(
      theme.palette.primary.main,
      theme.palette.mode === "light" ? 0.2 : 0.45
    );
    const accentGlow = alpha(
      accentTone,
      theme.palette.mode === "light" ? 0.16 : 0.36
    );
    return `linear-gradient(120deg, ${primaryGlow} 0%, ${accentGlow} 55%, ${base} 100%)`;
  }, [accentTone, theme]);

  useEffect(() => {
    let unsubscribeUserDoc = null;
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (unsubscribeUserDoc) {
        unsubscribeUserDoc();
        unsubscribeUserDoc = null;
      }
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        unsubscribeUserDoc = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            setUserProfile(docSnap.data());
          } else {
            setUserProfile({});
          }
        });
      } else {
        setUserProfile({});
      }
    });
    return () => {
      unsubscribeAuth();
      if (unsubscribeUserDoc) unsubscribeUserDoc();
    };
  }, []);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await signOut(auth);
    handleClose();
    navigate("/login");
  };

  const navigateAndClose = (path) => {
    handleClose();
    navigate(path);
  };

  const openDeleteDialog = () => {
    handleClose();
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteAccount = async () => {
    if (
      window.confirm(
        "Are you sure you want to permanently delete your account?"
      )
    ) {
      await deleteAccount();
      await signOut(auth);
      setDeleteDialogOpen(false); // Ensure dialog closes after account deletion
      navigate("/login");
    }
  };

  const isPathActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const navItems = [
    {
      label: "Dashboard",
      to: "/dashboard",
      icon: <DashboardIcon />,
      testId: "dashboard-button",
    },
    {
      label: "Today",
      to: "/today",
      icon: <TodayIcon />,
      testId: "navbar-today-button",
    },
    {
      label: "Bird's-Eye",
      to: "/birds-eye",
      icon: <InsightsIcon />,
      testId: "navbar-birds-eye-button",
    },
    {
      label: "Profile",
      to: "/profile",
      icon: <PersonIcon />,
      testId: "navbar-profile-button",
    },
    {
      label: "Agents",
      to: "/agents",
      icon: <PeopleIcon />,
      testId: "navbar-agents-button",
    },
    {
      label: "Breathing",
      to: "/breathing",
      icon: <SelfImprovementIcon />,
      testId: "navbar-breathing-button",
    },
    {
      label: "Trainer",
      to: "/trainer",
      icon: <FitnessCenterIcon />,
      testId: "navbar-trainer-button",
    },
    {
      label: "Therapist",
      to: "/therapist",
      icon: <PsychologyAltIcon />,
      testId: "navbar-therapist-button",
    },
  ];

  const navButtonBaseSx = {
    mr: 1.2,
    px: 2.4,
    py: 0.9,
    borderRadius: 999,
    fontWeight: 600,
    letterSpacing: 0.3,
    textTransform: "none",
    transition: "all 180ms ease",
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
    color:
      theme.palette.mode === "light"
        ? alpha(theme.palette.text.primary, 0.74)
        : alpha(theme.palette.common.white, 0.82),
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
    boxShadow: "none",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      boxShadow: theme.shadows[4],
    },
  };

  const getActiveButtonSx = (active) =>
    active
      ? {
          color: theme.palette.primary.contrastText,
          background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${accentTone})`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
          boxShadow: theme.shadows[8],
          "&:hover": {
            background: `linear-gradient(120deg, ${theme.palette.primary.dark}, ${accentTone})`,
            boxShadow: theme.shadows[10],
          },
        }
      : {};

  const menuPaperSx = {
    px: 1,
    py: 1,
    borderRadius: 3,
    background:
      theme.palette.mode === "light"
        ? `linear-gradient(135deg, ${alpha(
            theme.palette.background.paper,
            0.98
          )} 0%, ${alpha(accentTone, 0.12)} 100%)`
        : `linear-gradient(135deg, ${alpha(
            theme.palette.primary.dark ?? theme.palette.primary.main,
            0.55
          )} 0%, ${alpha(accentTone, 0.35)} 100%)`,
    boxShadow: theme.shadows[8],
    backdropFilter: "blur(22px)",
    border: `1px solid ${alpha(
      theme.palette.primary.main,
      theme.palette.mode === "light" ? 0.18 : 0.5
    )}`,
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        top: 0,
        zIndex: theme.zIndex.appBar,
        background: barBackground,
        backdropFilter: "blur(22px)",
        borderBottom: `1px solid ${alpha(
          theme.palette.primary.main,
          theme.palette.mode === "light" ? 0.16 : 0.45
        )}`,
        boxShadow: theme.shadows[6],
      }}
    >
      <Toolbar
        sx={{ gap: { xs: 1.5, md: 2.5 }, minHeight: { xs: 64, md: 72 } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, md: 2 },
            mr: { xs: 0, md: 1 },
          }}
        >
          <Avatar
            alt="MyDailyOne Logo"
            src={logo}
            data-testid="app-logo"
            sx={{
              width: 48,
              height: 48,
              boxShadow: theme.shadows[6],
              border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
            }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1 }}>
            <Typography
              variant="h6"
              data-testid="app-logo-typography"
              sx={{
                fontWeight: 700,
                letterSpacing: 0.5,
                color:
                  theme.palette.mode === "light"
                    ? alpha(theme.palette.text.primary, 0.85)
                    : alpha(theme.palette.common.white, 0.9),
              }}
            >
              MyDailyOne
            </Typography>
            <Typography
              component="span"
              variant="caption"
              role="note"
              aria-label="AI generated response disclaimer"
              sx={{
                fontSize: "0.58rem",
                color:
                  theme.palette.mode === "light"
                    ? alpha(theme.palette.text.primary, 0.58)
                    : alpha(theme.palette.common.white, 0.7),
                letterSpacing: 0.4,
                textTransform: "uppercase",
              }}
            >
              AI outputs may be unreliable.<br />Avoid sharing sensitive data; use at your own risk.
            </Typography>
          </Box>
        </Box>

        {isDesktop && user && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexGrow: 1,
              gap: 0.5,
            }}
          >
            {navItems.map((item) => {
              const active = isPathActive(item.to);
              return (
                <Button
                  key={item.to}
                  component={Link}
                  to={item.to}
                  startIcon={item.icon}
                  data-testid={item.testId}
                  sx={{ ...navButtonBaseSx, ...getActiveButtonSx(active) }}
                >
                  {item.label}
                </Button>
              );
            })}
          </Box>
        )}

        {!isDesktop && <Box sx={{ flexGrow: 1 }} />}

        {isPortrait && user && (
          <IconButton
            aria-label={chatExpanded ? "Hide Chat" : "Show Chat"}
            onClick={() => setChatExpanded((prev) => !prev)}
            sx={{
              mr: { xs: 0.5, md: 1 },
              color:
                theme.palette.mode === "light"
                  ? theme.palette.primary.main
                  : theme.palette.primary.light,
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              "&:hover": {
                backgroundColor: alpha(theme.palette.primary.main, 0.16),
              },
            }}
          >
            <ChatIcon fontSize="small" />
            {chatExpanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ExpandLessIcon fontSize="small" />
            )}
          </IconButton>
        )}

        {user ? (
          <div>
            <IconButton
              onClick={handleMenu}
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              color="inherit"
            >
              {userProfile.photoURL || user?.photoURL ? (
                <Avatar
                  src={userProfile.photoURL || user?.photoURL}
                  alt={userProfile.firstname || user?.displayName || ""}
                />
              ) : (
                <AccountCircle />
              )}
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{ sx: menuPaperSx }}
            >
              <MenuItem disableRipple onClick={(e) => e.stopPropagation()}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mode === "dark"}
                      onChange={toggleColorMode}
                      aria-label="toggle dark mode"
                    />
                  }
                  label="Dark Mode"
                />
              </MenuItem>
              <MenuItem
                disableRipple
                onClick={(e) => e.stopPropagation()}
                data-testid="menu-chat-visibility-item"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={chatExpanded}
                      onChange={(e) => setChatExpanded(e.target.checked)}
                      data-testid="menu-chat-visibility-switch"
                    />
                  }
                  label={chatExpanded ? "Hide Chat" : "Show Chat"}
                  data-testid="menu-chat-visibility-label"
                />
              </MenuItem>
              {!isDesktop && (
                <>
                  <MenuItem
                    onClick={() => navigateAndClose("/dashboard")}
                    data-testid="menu-dashboard"
                  >
                    Dashboard
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/today"
                    onClick={() => navigateAndClose("/today")}
                    data-testid="menu-today" // Added data-testid
                  >
                    Today
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/birds-eye"
                    onClick={() => navigateAndClose("/birds-eye")}
                    data-testid="menu-birds-eye"
                  >
                    Bird's-Eye
                  </MenuItem>
                  <MenuItem
                    onClick={() => navigateAndClose("/profile")}
                    data-testid="menu-profile"
                  >
                    Profile
                  </MenuItem>
                  {/*
                <MenuItem onClick={() => navigateAndClose("/profile/facts")}>
                  Personal Info
                </MenuItem>
              */}
                  <MenuItem
                    onClick={() => navigateAndClose("/agents")}
                    data-testid="menu-agents"
                  >
                    Agents
                  </MenuItem>
                  <MenuItem
                    component={Link}
                    to="/therapist"
                    onClick={() => navigateAndClose("/therapist")}
                    data-testid="menu-therapist"
                  >
                    Therapist
                  </MenuItem>
                </>
              )}
              <MenuItem
                disableRipple
                onClick={(e) => e.stopPropagation()}
                data-testid="menu-edit-images-item"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showImageButtons}
                      onChange={(e) => setShowImageButtons(e.target.checked)}
                      data-testid="menu-edit-images-switch" // Updated data-testid
                    />
                  }
                  label="Edit Images"
                  data-testid="menu-edit-images-label" // Added data-testid to label as well
                />
              </MenuItem>
              <MenuItem
                disableRipple
                onClick={(e) => e.stopPropagation()}
                data-testid="menu-show-timer-item"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showTimer}
                      onChange={(e) => setShowTimer(e.target.checked)}
                      data-testid="menu-show-timer-switch" // Updated data-testid
                    />
                  }
                  label="Show Timer"
                  data-testid="menu-show-timer-label" // Added data-testid to label as well
                />
              </MenuItem>
              <MenuItem
                disableRipple
                onClick={(e) => e.stopPropagation()}
                data-testid="menu-hide-points-item"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showPoints}
                      onChange={(e) => setShowPoints(e.target.checked)}
                      data-testid="menu-hide-points-switch" // Updated data-testid
                    />
                  }
                  label="Show Points"
                  data-testid="menu-hide-points-label"
                />
              </MenuItem>
              <MenuItem
                disableRipple
                onClick={(e) => e.stopPropagation()}
                data-testid="menu-show-images-item"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showImages}
                      onChange={(e) => setShowImages(e.target.checked)}
                      data-testid="menu-show-images-switch"
                    />
                  }
                  label="Show Images"
                  data-testid="menu-show-images-label"
                />
              </MenuItem>
              <MenuItem
                disableRipple
                onClick={(e) => e.stopPropagation()}
                data-testid="menu-node-buttons-item"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={showNodeButtons}
                      onChange={(e) => setShowNodeButtons(e.target.checked)}
                      data-testid="menu-node-buttons-switch"
                    />
                  }
                  label="Edit"
                  data-testid="menu-node-buttons-label"
                />
              </MenuItem>
              <Divider /> {/* Added Divider */}
              <MenuItem
                component={Link}
                to="/terms-of-service"
                onClick={() => navigateAndClose("/terms-of-service")}
                data-testid="menu-terms" // Added data-testid
              >
                Terms of Service
              </MenuItem>
              <MenuItem
                component={Link}
                to="/privacy-policy"
                onClick={() => navigateAndClose("/privacy-policy")}
                data-testid="menu-privacy" // Added data-testid
              >
                Privacy Policy
              </MenuItem>
              <MenuItem
                component={Link}
                to="/research-consent"
                onClick={() => navigateAndClose("/research-consent")}
                data-testid="menu-research" // Added data-testid
              >
                Research Consent
              </MenuItem>
              <MenuItem
                onClick={openDeleteDialog}
                data-testid="menu-delete-account"
              >
                Delete Account
              </MenuItem>
              <Divider />
              {/* <MenuItem
                onClick={() => {
                  startTutorial(location.pathname);
                  handleClose();
                }}
                data-testid="menu-start-tutorial"
              >
                Start/Resume Tutorial
              </MenuItem>{" "} */}
              {/* Modified onClick and added data-testid */}
              {/* <MenuItem
                onClick={() => {
                  resetTutorialProgress();
                  handleClose();
                }}
              >
                Reset Tutorial
              </MenuItem> */}
              <Divider />{" "}
              {/* Added another Divider for separation before Logout */}
              <MenuItem onClick={handleLogout} data-testid="menu-logout">
                Logout
              </MenuItem>
            </Menu>
          </div>
        ) : (
          <>
            <Button
              onClick={() => navigate("/login")}
              sx={{
                ...navButtonBaseSx,
                mr: 1,
                backgroundColor: "transparent",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.12),
                },
              }}
            >
              Login
            </Button>
            <Button
              onClick={() => navigate("/register")}
              sx={{
                ...navButtonBaseSx,
                color: theme.palette.primary.contrastText,
                background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${accentTone})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
                boxShadow: theme.shadows[6],
                "&:hover": {
                  background: `linear-gradient(120deg, ${theme.palette.primary.dark}, ${accentTone})`,
                  boxShadow: theme.shadows[9],
                },
              }}
            >
              Register
            </Button>
          </>
        )}
      </Toolbar>
      <DeleteAccountDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteAccount}
      />
    </AppBar>
  );
};

export default Navbar;
